<template>
    <div class="swfclip-animation">
        <div ref="hostRef" class="swfclip-host"></div>
        <div v-if="showStatus && (loading || error)" class="swfclip-status">
            <span v-if="loading">SWFClip 加载中…</span>
            <span v-else>{{ error }}</span>
        </div>
    </div>
</template>

<script setup lang="ts">
import {
    Application,
    Container,
    Rectangle,
    Sprite,
    Texture,
    type Ticker,
} from "pixi.js";
import { onBeforeUnmount, onMounted, ref, watch } from "vue";
import { loadSwfClipAtlas } from "../ui/swfClipPreload";
import { AsyncResource } from "../ui/asyncResource";
import {
    findSwfClipSequence,
    getSwfClipSequence,
    loadSwfClip,
    shouldLoopSwfClipSequence,
    swfClipAnimationBounds,
    swfClipFrameDurationSeconds,
    swfClipSequencePrefixDurationSeconds,
    swfClipSequenceForSkill,
    type LoadedSwfClip,
    type SwfClipFrame,
    type SwfClipSequence,
} from "../core/swfClipAnimation";
import type { Skill } from "../core/skill";
import {
    SWF_STAGE_HEIGHT,
    SWF_STAGE_WIDTH,
} from "../core/swfBattleStage";
import type { BattleStageBounds, BattleStageRenderViewport } from "../core/battleStageViewport";

const props = withDefaults(
    defineProps<{
        petId?: string | number;
        baseUrl?: string;
        transform?: boolean;
        battleSide?: "left" | "right" | "center";
        initialSequence?: string;
        showStatus?: boolean;
        viewport?: BattleStageRenderViewport;
    }>(),
    {
        petId: "300",
        transform: false,
        battleSide: "center",
        initialSequence: "standby",
        showStatus: false,
    },
);

const emit = defineEmits<{
    (event: "loaded", clip: LoadedSwfClip): void;
    (event: "bounds", bounds: BattleStageBounds): void;
    (event: "error", error: Error): void;
}>();

const hostRef = ref<HTMLElement | null>(null);
const loading = ref(true);
const error = ref("");

let app: Application | null = null;
let stage: Container | null = null;
let sprite: Sprite | null = null;
let atlasTextures: Texture[] = [];
let clip: LoadedSwfClip | null = null;
let selectedSequence = props.initialSequence;
let frameIndex = 0;
let frameElapsed = 0;
let disposed = false;
let loadGeneration = 0;
let playbackResolve: (() => void) | null = null;
let readyResolve: (() => void) | null = null;
let ready: Promise<void>;
const frameTextures = new Map<string, Texture>();

function resetReady() {
    readyResolve?.();
    ready = new Promise<void>((resolve) => {
        readyResolve = resolve;
    });
}

resetReady();

function resolvePlayback() {
    playbackResolve?.();
    playbackResolve = null;
}

function viewport() {
    const width = Number.isFinite(props.viewport?.width) && props.viewport!.width > 0
        ? props.viewport!.width
        : hostRef.value?.clientWidth || SWF_STAGE_WIDTH;
    const height = Number.isFinite(props.viewport?.height) && props.viewport!.height > 0
        ? props.viewport!.height
        : hostRef.value?.clientHeight || SWF_STAGE_HEIGHT;
    return {
        width,
        height,
        x: Number.isFinite(props.viewport?.x) ? props.viewport!.x : SWF_STAGE_WIDTH / 2,
        y: Number.isFinite(props.viewport?.y) ? props.viewport!.y : SWF_STAGE_HEIGHT / 2,
        scale: Number.isFinite(props.viewport?.scale) && props.viewport!.scale > 0
            ? props.viewport!.scale
            : 1,
    };
}

function currentSequence(): SwfClipSequence | null {
    return clip ? getSwfClipSequence(clip.meta, selectedSequence) : null;
}

function frameTexture(sequenceName: string, index: number, frame: SwfClipFrame) {
    const atlasTexture = atlasTextures[frame.atlas ?? 0];
    if (!atlasTexture) return Texture.EMPTY;
    const key = `${sequenceName}:${index}`;
    const cached = frameTextures.get(key);
    if (cached) return cached;
    const texture = new Texture({
        source: atlasTexture.source,
        frame: new Rectangle(frame.x, frame.y, frame.width, frame.height),
    });
    frameTextures.set(key, texture);
    return texture;
}

function renderFrame() {
    const current = currentSequence();
    const frame = current?.frames[frameIndex];
    if (!sprite || !clip || !frame) return;
    const camera = viewport();
    const scale = camera.scale;
    // All SWFClip manifests use the shared 960px logical stage. Keep the
    // mirror registration anchored to that contract even during development
    // when a caller may have constructed an unvalidated clip object.
    const stageWidth = SWF_STAGE_WIDTH;
    const mirrored = props.transform;
    const offsetX = frame.offsetX;
    const offsetY = frame.offsetY;
    sprite.texture = frameTexture(current.name, frameIndex, frame);
    sprite.scale.set(mirrored ? -scale : scale, scale);
    sprite.position.set(
        camera.width / 2 + (mirrored ? stageWidth - offsetX - camera.x : offsetX - camera.x) * scale,
        camera.height / 2 + (offsetY - camera.y) * scale,
    );
}

function emitBounds() {
    if (!clip) return;
    emit("bounds", swfClipAnimationBounds(clip.meta, props.transform));
}

function tick(ticker: Ticker) {
    const current = currentSequence();
    if (!current || current.frames.length < 1 || !clip) return;
    const frameRate = Math.max(1, clip.meta.frameRate || 25);
    frameElapsed += ticker.deltaMS / 1000;
    while (true) {
        const frameDuration = swfClipFrameDurationSeconds(
            current.frames[frameIndex],
            frameRate,
        );
        if (frameElapsed < frameDuration) break;
        frameElapsed -= frameDuration;
        frameIndex += 1;
        if (frameIndex < current.frames.length) continue;
        if (shouldLoopSwfClipSequence(current)) {
            frameIndex = 0;
            continue;
        }
        selectedSequence = "standby";
        frameIndex = 0;
        frameElapsed = 0;
        resolvePlayback();
        break;
    }
    renderFrame();
}

const application = new AsyncResource<Application>(
    async () => {
        const candidate = new Application();
        await candidate.init({
            resizeTo: hostRef.value!,
            backgroundAlpha: 0,
            antialias: true,
            autoDensity: true,
            resolution: typeof window === "undefined" ? 1 : Math.max(1, window.devicePixelRatio || 1),
            preference: "webgl",
        });
        return candidate;
    },
    (candidate) => candidate.destroy({ removeView: true }, { children: true, texture: false, textureSource: false }),
);

async function ensureApp() {
    if (app || disposed || !hostRef.value) return;
    const initialized = await application.get();
    if (!initialized || disposed || app || !hostRef.value) return;
    app = initialized;
    hostRef.value.appendChild(app.canvas);
    stage = new Container();
    sprite = new Sprite(Texture.EMPTY);
    stage.addChild(sprite);
    app.stage.addChild(stage);
    app.ticker.add(tick);
}

async function load() {
    const generation = ++loadGeneration;
    resetReady();
    loading.value = true;
    error.value = "";
    resolvePlayback();
    frameTextures.forEach((texture) => texture.destroy(false));
    frameTextures.clear();
    atlasTextures = [];
    clip = null;
    try {
        await ensureApp();
        if (disposed || generation !== loadGeneration) return;
        const loaded = await loadSwfClip(props.baseUrl ?? "", String(props.petId));
        const atlases = await Promise.all(
            loaded.atlases.map((url) => loadSwfClipAtlas(url)),
        );
        if (disposed || generation !== loadGeneration) return;
        clip = loaded;
        atlasTextures = atlases;
        selectedSequence = findSwfClipSequence(loaded.meta, props.initialSequence)
            ? props.initialSequence
            : "standby";
        frameIndex = 0;
        frameElapsed = 0;
        loading.value = false;
        renderFrame();
        emitBounds();
        emit("loaded", loaded);
    } catch (cause) {
        if (disposed || generation !== loadGeneration) return;
        loading.value = false;
        error.value = cause instanceof Error ? cause.message : "SWFClip 加载失败";
        emit("error", cause instanceof Error ? cause : new Error(error.value));
    } finally {
        if (generation === loadGeneration) {
            readyResolve?.();
            readyResolve = null;
        }
    }
}

async function play(name: string): Promise<void> {
    await ready;
    if (disposed || !clip) return;
    resolvePlayback();
    const sequence = findSwfClipSequence(clip.meta, name);
    if (!sequence || sequence.frames.length === 0) {
        selectedSequence = "standby";
        frameIndex = 0;
        frameElapsed = 0;
        renderFrame();
        return;
    }
    selectedSequence = name;
    frameIndex = 0;
    frameElapsed = 0;
    renderFrame();
    if (shouldLoopSwfClipSequence(sequence)) return;
    return new Promise<void>((resolve) => {
        playbackResolve = resolve;
    });
}

onMounted(() => {
    void load();
});

watch(
    () => [props.petId, props.baseUrl] as const,
    () => {
        if (!disposed) void load();
    },
);

watch(
    () => [
        props.viewport?.width,
        props.viewport?.height,
        props.viewport?.x,
        props.viewport?.y,
        props.viewport?.scale,
    ] as const,
    () => renderFrame(),
);

watch(
    () => props.transform,
    () => {
        renderFrame();
        emitBounds();
    },
);

onBeforeUnmount(() => {
    disposed = true;
    loadGeneration += 1;
    resolvePlayback();
    readyResolve?.();
    readyResolve = null;
    frameTextures.forEach((texture) => texture.destroy(false));
    frameTextures.clear();
    atlasTextures = [];
    if (app) {
        app.ticker.remove(tick);
        app = null;
    }
    application.dispose();
    stage = null;
    sprite = null;
});

defineExpose({
    play,
    playAttack: (skill: Skill) => play(swfClipSequenceForSkill(skill)),
    playHit: () => play("hited"),
    getSequenceFrameCount: (name: string) =>
        clip ? (findSwfClipSequence(clip.meta, name)?.frames.length ?? 0) : 0,
    getAttackLeadDurationMs: (skill: Skill, hitFrameCount: number) => {
        if (!clip) return 0;
        const attackName = swfClipSequenceForSkill(skill);
        const attackFrameCount = findSwfClipSequence(clip.meta, attackName)?.frames.length ?? 0;
        const leadFrameCount = Math.max(0, attackFrameCount - hitFrameCount);
        return swfClipSequencePrefixDurationSeconds(
            clip.meta,
            attackName,
            leadFrameCount,
        ) * 1000;
    },
    playIdle: () => play("standby"),
});
</script>

<style scoped>
.swfclip-animation,
.swfclip-host {
    position: relative;
    width: 100%;
    height: 100%;
    min-height: 0;
    overflow: hidden;
    background: transparent;
}

.swfclip-host :deep(canvas) {
    display: block;
    width: 100%;
    height: 100%;
    pointer-events: none;
}

.swfclip-status {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    color: rgba(220, 230, 245, 0.8);
    font-size: calc(var(--ui-unit) * 12);
    pointer-events: none;
}
</style>
